import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Header } from '@/components/Header';
import { Footer } from '@/components/Footer';
import { DogSchool } from '@/data/dogSchools';
import { getSupabase } from '@/integrations/supabase/client';
import { mapSupabaseRowToDogSchool } from '@/utils/supabaseMappers';
import { EnhancedSEOHead } from '@/components/SEO/EnhancedSEOHead';
import { LocalBusinessSchema } from '@/components/SEO/LocalBusinessSchema';
import { BreadcrumbSchema } from '@/components/SEO/BreadcrumbSchema';
import { GeoMetaTags } from '@/components/SEO/GeoMetaTags';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { MapPin, Phone, Mail, Globe, Clock, Star, ArrowLeft } from 'lucide-react';
import { OptimizedImage } from '@/components/OptimizedImage';
import { LoadingSpinner } from '@/components/LoadingSpinner';

const DogSchoolDetail = () => {
  const { city, schoolId } = useParams<{ city: string; schoolId: string }>();
  const navigate = useNavigate();
  const [school, setSchool] = useState<DogSchool | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchSchool = async () => {
      if (!schoolId) return;
      
      setLoading(true);
      setError(null);
      
      try {
        const supabase = getSupabase();
        const { data, error: fetchError } = await supabase
          .from('hundeschuleList')
          .select('*')
          .eq('google_id', schoolId)
          .single();

        if (fetchError) throw fetchError;
        if (!data) throw new Error('Hundeschule nicht gefunden');

        const mappedSchool = mapSupabaseRowToDogSchool(data);
        setSchool(mappedSchool);
      } catch (err) {
        console.error('Error fetching school:', err);
        setError('Hundeschule konnte nicht geladen werden');
      } finally {
        setLoading(false);
      }
    };

    fetchSchool();
  }, [schoolId]);

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col">
        <Header />
        <main className="flex-grow flex items-center justify-center">
          <LoadingSpinner />
        </main>
        <Footer />
      </div>
    );
  }

  if (error || !school) {
    return (
      <div className="min-h-screen flex flex-col">
        <Header />
        <main className="flex-grow container mx-auto px-4 py-8">
          <div className="text-center">
            <h1 className="text-2xl font-bold mb-4">Hundeschule nicht gefunden</h1>
            <Button onClick={() => navigate('/')}>Zurück zur Startseite</Button>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const seoTitle = `${school.name} in ${school.city} | Hundeschule ${school.state}`;
  const seoDescription = `${school.name} - Professionelle Hundeschule in ${school.city}, ${school.state}. ✓ ${school.googleReview}/5 Bewertung ✓ ${school.reviewCount} Rezensionen ✓ Jetzt kontaktieren!`;

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <EnhancedSEOHead
        title={seoTitle}
        description={seoDescription}
        keywords={`${school.name}, Hundeschule ${school.city}, Hundetrainer ${school.state}, Welpenschule ${school.postalCode}`}
        canonicalUrl={`/hundeschule/${city}/${schoolId}`}
      />
      <LocalBusinessSchema school={school} />
      <BreadcrumbSchema
        items={[
          { name: 'Startseite', url: '/' },
          { name: school.country === 'Germany' ? 'Deutschland' : school.country, url: '/germany' },
          { name: school.state, url: `/germany/${school.state.toLowerCase().replace(/\s+/g, '-')}` },
          { name: school.city, url: `/germany/${school.state.toLowerCase().replace(/\s+/g, '-')}` },
          { name: school.name, url: `/hundeschule/${city}/${schoolId}` }
        ]}
      />
      <GeoMetaTags
        placename={school.city}
        region={school.state}
        country="DE"
        latitude={school.latitude}
        longitude={school.longitude}
      />

      <Header />

      <main className="flex-grow container mx-auto px-4 py-8">
        {/* Back Button */}
        <Button
          variant="ghost"
          onClick={() => navigate(-1)}
          className="mb-4"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Zurück
        </Button>

        {/* Hero Section */}
        <Card className="mb-8 overflow-hidden">
          <div className="grid md:grid-cols-2 gap-6">
            {/* Image */}
            <div className="relative h-64 md:h-full min-h-[300px]">
              <OptimizedImage
                src={school.imageUrl || ''}
                alt={school.imageAlt || `${school.name} in ${school.city}`}
                className="w-full h-full object-cover"
              />
            </div>

            {/* Info */}
            <CardContent className="p-6">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h1 className="text-3xl font-bold mb-2">{school.name}</h1>
                  <div className="flex items-center gap-2 mb-2">
                    <Star className="h-5 w-5 fill-yellow-400 text-yellow-400" />
                    <span className="font-semibold text-lg">{school.googleReview}</span>
                    <span className="text-gray-600">({school.reviewCount} Bewertungen)</span>
                  </div>
                </div>
              </div>

              {/* Contact Info */}
              <div className="space-y-3 mb-6">
                <div className="flex items-start gap-2">
                  <MapPin className="h-5 w-5 text-primary mt-1 flex-shrink-0" />
                  <div>
                    <p className="font-medium">{school.street}</p>
                    <p className="text-gray-600">{school.postalCode} {school.city}</p>
                  </div>
                </div>

                {school.phone && (
                  <div className="flex items-center gap-2">
                    <Phone className="h-5 w-5 text-primary flex-shrink-0" />
                    <a href={`tel:${school.phone}`} className="hover:text-primary">
                      {school.phone}
                    </a>
                  </div>
                )}

                {school.email && (
                  <div className="flex items-center gap-2">
                    <Mail className="h-5 w-5 text-primary flex-shrink-0" />
                    <a href={`mailto:${school.email}`} className="hover:text-primary">
                      {school.email}
                    </a>
                  </div>
                )}

                {school.website && (
                  <div className="flex items-center gap-2">
                    <Globe className="h-5 w-5 text-primary flex-shrink-0" />
                    <a
                      href={school.website}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="hover:text-primary"
                    >
                      Website besuchen
                    </a>
                  </div>
                )}
              </div>

              {/* Action Buttons */}
              <div className="flex flex-wrap gap-3">
                {school.phone && (
                  <Button asChild>
                    <a href={`tel:${school.phone}`}>
                      <Phone className="mr-2 h-4 w-4" />
                      Jetzt anrufen
                    </a>
                  </Button>
                )}
                {school.website && (
                  <Button variant="outline" asChild>
                    <a href={school.website} target="_blank" rel="noopener noreferrer">
                      Zur Website
                    </a>
                  </Button>
                )}
              </div>
            </CardContent>
          </div>
        </Card>

        {/* Description */}
        {school.description && (
          <Card className="mb-8">
            <CardContent className="p-6">
              <h2 className="text-2xl font-bold mb-4">Über uns</h2>
              <p className="text-gray-700 leading-relaxed whitespace-pre-line">{school.description}</p>
            </CardContent>
          </Card>
        )}

        {/* Opening Hours */}
        {school.openingHours && (
          <Card className="mb-8">
            <CardContent className="p-6">
              <div className="flex items-center gap-2 mb-4">
                <Clock className="h-5 w-5 text-primary" />
                <h2 className="text-2xl font-bold">Öffnungszeiten</h2>
              </div>
              <p className="text-gray-700">{school.openingHours.de}</p>
            </CardContent>
          </Card>
        )}

        {/* Features */}
        {school.features && (
          <Card className="mb-8">
            <CardContent className="p-6">
              <h2 className="text-2xl font-bold mb-4">Ausstattung & Services</h2>
              <div className="flex flex-wrap gap-2">
                {school.features.hasPrivateLessons && <Badge>Einzeltraining</Badge>}
                {school.features.hasGroupLessons && <Badge>Gruppentraining</Badge>}
                {school.features.hasDogLicense && <Badge>Hundeführerschein</Badge>}
                {school.features.hasFreeParking && <Badge>Kostenlose Parkplätze</Badge>}
                {school.features.isWheelchairAccessible && <Badge>Barrierefrei</Badge>}
                {school.features.hasMultiLanguageSupport && <Badge>Mehrsprachig</Badge>}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Reviews */}
        {school.reviews && school.reviews.length > 0 && (
          <Card>
            <CardContent className="p-6">
              <h2 className="text-2xl font-bold mb-4">Bewertungen</h2>
              <div className="space-y-4">
                {school.reviews.slice(0, 5).map((review, index) => (
                  <div key={index} className="border-b pb-4 last:border-b-0">
                    <p className="text-gray-700 italic">"{review}"</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </main>

      <Footer />
    </div>
  );
};

export default DogSchoolDetail;
